const DAY = 24 * 60 * 60 * 1000;

export interface CaseKpiSelection {
  key: string;
  label: string;
  cases: any[];
}

interface CaseAnalyticsProps {
  cases: any[];
  /** Indicador selecionado no momento (filtra a lista da página) */
  activeKey?: string | null;
  /** Chamado ao clicar num indicador; null limpa o filtro */
  onSelect?: (selection: CaseKpiSelection | null) => void;
}

/** Indicadores da página de Processos; o selecionado filtra a lista logo abaixo. */
export const getCaseKpis = (cases: any[]) => {
  const now = new Date();
  const nextWeek = new Date(now.getTime() + 7 * DAY);
  const isOpen = (c: any) => c.status !== 'Concluído';

  return [
    { key: 'total', label: 'Total', hint: 'processos ativos', cases, tone: 'text-slate-900', pulse: false },
    {
      key: 'overdue',
      label: 'Em atraso',
      hint: 'prazo vencido',
      cases: cases.filter((c) => c.deadline && new Date(c.deadline) < now && isOpen(c)),
      tone: 'text-red-600',
      pulse: true,
    },
    {
      key: 'upcoming',
      label: 'Próximos 7 dias',
      hint: 'prazos a vencer',
      cases: cases.filter((c) => {
        if (!c.deadline || !isOpen(c)) return false;
        const d = new Date(c.deadline);
        return d >= now && d <= nextWeek;
      }),
      tone: 'text-amber-600',
      pulse: true,
    },
    {
      key: 'risk',
      label: 'Alto risco',
      hint: 'risco alto ou crítico',
      cases: cases.filter((c) => c.risk_classification === 'alto' || c.risk_classification === 'critico'),
      tone: 'text-orange-600',
      pulse: false,
    },
    {
      key: 'new',
      label: 'Novos no mês',
      hint: 'cadastrados em ' + now.toLocaleDateString('pt-BR', { month: 'long' }),
      cases: cases.filter((c) => {
        const d = new Date(c.created_at);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }),
      tone: 'text-slate-900',
      pulse: false,
    },
  ];
};

export const CaseAnalytics = ({ cases, activeKey, onSelect }: CaseAnalyticsProps) => {
  const kpis = getCaseKpis(cases);

  return (
    <section className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-5">
      {kpis.map((kpi, index) => {
        const active = activeKey === kpi.key;
        return (
          <button
            key={kpi.key}
            type="button"
            onClick={() =>
              onSelect?.(active || kpi.key === 'total' ? null : { key: kpi.key, label: kpi.label, cases: kpi.cases })
            }
            className={`group relative px-4 py-4 text-left transition-colors duration-200 animate-in fade-in slide-in-from-bottom-1 fill-mode-both last:col-span-2 sm:last:col-span-1 ${
              active ? 'bg-[#faf7f2]' : 'bg-white hover:bg-[#faf7f2]'
            }`}
            style={{ animationDelay: `${index * 40}ms` }}
          >
            {active && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#c4ab83]" />}
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              {kpi.pulse && kpi.cases.length > 0 && (
                <span className={`h-1.5 w-1.5 animate-pulse rounded-full ${kpi.key === 'overdue' ? 'bg-red-500' : 'bg-amber-500'}`} />
              )}
              {kpi.label}
            </div>
            <div className={`mt-1.5 text-2xl font-semibold tabular-nums tracking-tight ${kpi.cases.length ? kpi.tone : 'text-slate-900'}`}>
              {kpi.cases.length}
            </div>
            <div className="mt-0.5 text-[11px] text-slate-400">{kpi.hint}</div>
          </button>
        );
      })}
    </section>
  );
};
